import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { LandingModelService } from './landingModel.service';
import { isPlatformBrowser } from '@angular/common';

declare var window: any;

interface TimmingsTrackingData {
  cc: string;
  ch: string;
  pr: string;
  fl: string;
  url: string;
  ab: string;
  xClient: string;
  userid: string;
  tripStatus: string;
  lname: string;
  ltype: string;
  companyId: string;
  platform: string;
  fromServerCache: string;
  guiVersion: string;
  serviceVersion: string;
  localWebView: string;
  isBot: string;
}
interface ProductChangeTimming {
  selectedProduct: string;
  fromServerCache: string;
  fromBrowserCache: string;
  pageViewId: string
}


@Injectable({
  providedIn: 'root'
})
export class CustomTimmingsService {

  readonly WEB_VITALS = {
    CUMULATIVE_LAYOUT_SHIFT: "CLS",
    FIRST_INPUT_DELAY: "FID",
    LARGEST_CONTENTFUL_PAINT: "LCP",
    FIRST_CONTENTFUL_PAINT: "FCP",
    TIME_TO_FIRST_BYTE: "TTFB",
  }

  retries: number = 0;

  constructor(@Inject(PLATFORM_ID) private platformId: string,
              public landingModelService: LandingModelService) { }


  customTimmingsIsAvailable(): boolean {
    return (
      typeof(window) !== 'undefined'
      && !!window.customTimmings
      && !!window.customTimmings.trackMainFeature
      && !!window.customTimmings.trackBounce
      && !!window.customTimmings.trackTimmings
    );
  };

  getMillisecondsInteger(number) {
    return Math.round(number * 1000);
  }

  getTimesArray(times: any) {
    return Object.keys(times).map(key => ({
      metricName: key,
      time: this.getMillisecondsInteger(times[key])
    }));
  }

  getDataToTrack(): TimmingsTrackingData {
    const upaData = window.upaData;
    const landingModel = this.landingModelService.getLandingModel();
    const trackingInfo = landingModel?.newRelicData?.tracking_info;
    return {
        cc: upaData.cc,
        ch: upaData.ch,
        pr: upaData.pr,
        fl: upaData.fl,
        localWebView: this.landingModelService.isCachedLanding() ? "true" : "false",
        url: upaData.url,
        xClient: upaData.xClient,
        ab: landingModel?.abTests?.ab || "",
        userid: upaData.userid,
        tripStatus: upaData.trip_status,
        lname: upaData.lname,
        ltype: upaData.ltype,
        companyId: upaData.company_id,
        platform: landingModel?.device?.platform?.toString() || "",
        fromServerCache: trackingInfo.cacheResponse.toString(),
        guiVersion: landingModel?.guiVersion,
        serviceVersion: trackingInfo.serviceVersion,
        isBot: landingModel.isBot?.toString() || ""
    };
  }

  trackLandingTimings(timesObject): void {
    const upaData = window.upaData;
    this.retries++;
    if (isPlatformBrowser(this.platformId)) {
      if (upaData && this.customTimmingsIsAvailable() && upaData.robot === "false") {
        window.customTimmings.trackTimmings(this.getTimesArray(timesObject), this.getDataToTrack(), upaData.xuowHeader, upaData.pageview_id);
      } else {
        if (window.newrelic != null) {
          window.newrelic.addPageAction("error", {
            type: 'times', 
            metric: 'timings', 
            customTimmingsIsAvailable: this.customTimmingsIsAvailable(), 
            isPlatformBrowser: isPlatformBrowser(this.platformId), 
            upaDataExist: upaData != null,
            robot: upaData?.robot,
            localWebView: this.landingModelService.isCachedLanding()
          });
        }

        if (this.retries <= 5) {
          setTimeout(() => this.trackLandingTimings(timesObject), 1000);
        }
      }
    }
  }

  trackProductChangePageLoad(productChangeTimming: ProductChangeTimming, sectionTime: number) {
    const upaData = window.upaData;
    const headers: { [key: string]: string } = { 'Accept': 'application/json', 'type': 'application/json' };

    if (isPlatformBrowser(this.platformId) && upaData && upaData.robot === "false" && !this.landingModelService.isCachedLanding()) {
      const productChangeTime = {
        pageLoaded: Math.round(sectionTime)
      };
      let upaTopicData = {
        data: {...this.getDataToTrack(), ...productChangeTimming, changeProduct: "true"},
        durations: productChangeTime,
        uow: upaData.xuowHeader,
        pageview_id: productChangeTimming.pageViewId
      };
      const blob = new Blob([JSON.stringify(upaTopicData)], headers);
      navigator.sendBeacon('/hermes-service/topic/shopping_timmings', blob);
    }
  }

  trackBounce(): void {
    const upaData = window.upaData;
    if (isPlatformBrowser(this.platformId)) {
      if (upaData && this.customTimmingsIsAvailable() && upaData.robot === "false") {
        window.customTimmings.trackBounce(this.getDataToTrack(), upaData.xuowHeader, upaData.pageview_id);
      } else {
        if (window.newrelic != null) {
          window.newrelic.addPageAction("error", {
            type: 'times', 
            metric: 'bounce', 
            customTimmingsIsAvailable: this.customTimmingsIsAvailable(), 
            isPlatformBrowser: isPlatformBrowser(this.platformId), 
            upaDataExist: upaData != null,
            robot: upaData?.robot
          });
        }
      }
    }
  }
}
